import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);
    this.addEventListener('change', this.#handleUpsellVariantChange);
    this.addEventListener('click', this.#handleUpsellAddClick);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.removeEventListener('change', this.#handleUpsellVariantChange);
    this.removeEventListener('click', this.#handleUpsellAddClick);
  }

  /**
   * Updates the upsell card price, image, and button variant ID when the shopper changes the variant dropdown.
   * @param {Event} event
   */
  #handleUpsellVariantChange = (event) => {
    const select = event.target instanceof Element ? event.target.closest('[data-sct-upsell-select]') : null;
    if (!(select instanceof HTMLSelectElement)) return;
    const card = select.closest('[data-sct-upsell-card]');
    if (!card) return;
    const opt = select.selectedOptions[0];
    if (!opt) return;

    const addBtn = card.querySelector('[data-sct-upsell-add]');
    if (addBtn instanceof HTMLElement) {
      addBtn.dataset.variantId = opt.value;
    }
    const priceEl = card.querySelector('[data-sct-upsell-price]');
    if (priceEl && opt.dataset.price) {
      priceEl.textContent = opt.dataset.price;
    }
    const imgEl = card.querySelector('[data-sct-upsell-img]');
    if (imgEl instanceof HTMLImageElement && opt.dataset.img) {
      imgEl.src = opt.dataset.img;
    }
  };

  /**
   * Handles 1-click "+ Add" from the Cart Drawer Upsell Tray and morphs the drawer section.
   * @param {MouseEvent} event
   */
  #handleUpsellAddClick = async (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-sct-upsell-add]') : null;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
    event.preventDefault();

    const card = btn.closest('[data-sct-upsell-card]');
    const select = card?.querySelector('[data-sct-upsell-select]');
    const variantId = select instanceof HTMLSelectElement ? select.value : btn.dataset.variantId;
    if (!variantId) return;

    const originalText = btn.textContent || '+ Add';
    btn.disabled = true;
    btn.textContent = 'Adding…';

    const deferredAddPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ id: String(variantId), quantity: 1 }],
        promise: deferredAddPromise.promise,
      })
    );

    try {
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.json`;
      const addRes = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: 'cart-drawer-section',
          sections_url: window.location.pathname,
        }),
      });
      const parsedResponse = await addRes.json();
      if (!addRes.ok || parsedResponse.status) {
        throw new Error(parsedResponse.description || parsedResponse.message || 'Could not add item');
      }

      const cartRes = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartRes.json();

      deferredAddPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-drawer-upsell',
          itemCount: cart.item_count,
          sections: parsedResponse.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferredAddPromise.reject(error);
      btn.disabled = false;
      btn.textContent = originalText;
    }
  };

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
